import { cva, type VariantProps } from "class-variance-authority";
import { type ButtonHTMLAttributes, type FC, type ReactNode, type Ref } from "react";
import { cn } from "../lib/utils";
import { _BaseButton } from "./_BaseButton";
import { Spinner } from "./Spinner";

const colorClasses = {
	primary: "",
	secondary: "",
	confirm: "",
	inverse: "",
	danger: "",
};

export type ButtonColor = keyof typeof colorClasses;
export const BUTTON_COLORS = Object.keys(colorClasses) as readonly ButtonColor[];

const buttonSizeToSpinnerSize = { sm: "sm", md: "md" } as const;

const buttonVariants = cva(
	"relative shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer select-none transition-colors disabled:cursor-not-allowed disabled:opacity-[0.45]",
	{
		variants: {
			color: colorClasses,
			variant: {
				solid: "rounded-[4px] font-semibold text-inverse",
				outline: "rounded-[4px] font-semibold border bg-transparent",
				ghost: "bg-transparent",
			},
			size: {
				sm: "h-7 px-3 text-xs",
				md: "h-[34px] px-4 text-sm",
			},
			pill: {
				true: "rounded-full",
			},
		},
		compoundVariants: [
			{
				color: "primary",
				variant: "solid",
				className:
					"bg-button-primary enabled:hover:bg-button-primary-hover enabled:active:bg-button-primary-active",
			},
			{
				color: "secondary",
				variant: "solid",
				className:
					"bg-button-secondary enabled:hover:bg-button-secondary-hover enabled:active:bg-button-secondary-active",
			},
			{
				color: "confirm",
				variant: "solid",
				className:
					"bg-button-confirm enabled:hover:bg-button-confirm-hover enabled:active:bg-button-confirm-active",
			},
			{
				color: "inverse",
				variant: "solid",
				className:
					"bg-button-inverse text-primary enabled:hover:bg-button-inverse-hover enabled:active:bg-button-inverse-active",
			},
			{
				color: "danger",
				variant: "solid",
				className:
					"bg-button-danger enabled:hover:bg-button-danger-hover enabled:active:bg-button-danger-active",
			},
			{
				color: "primary",
				variant: "outline",
				className:
					"border-button-primary text-button-primary enabled:hover:border-button-primary-hover enabled:hover:text-button-primary-hover enabled:active:border-button-primary-active enabled:active:text-button-primary-active",
			},
			{
				color: "secondary",
				variant: "outline",
				className:
					"border-button-secondary text-secondary enabled:hover:border-button-secondary-hover enabled:active:border-button-secondary-active",
			},
			{
				color: "confirm",
				variant: "outline",
				className:
					"border-button-confirm text-button-confirm enabled:hover:border-button-confirm-hover enabled:hover:text-button-confirm-hover enabled:active:border-button-confirm-active enabled:active:text-button-confirm-active",
			},
			{
				color: "inverse",
				variant: "outline",
				className:
					"border-button-inverse text-button-inverse enabled:hover:border-button-inverse-hover enabled:hover:text-button-inverse-hover enabled:active:border-button-inverse-active enabled:active:text-button-inverse-active",
			},
			{
				color: "danger",
				variant: "outline",
				className:
					"border-button-danger text-button-danger enabled:hover:border-button-danger-hover enabled:hover:text-button-danger-hover enabled:active:border-button-danger-active enabled:active:text-button-danger-active",
			},
		],
		defaultVariants: {
			variant: "solid",
			size: "md",
			pill: false,
		},
	},
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

type ButtonCommonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color"> & {
	/**
	 * Vertical size of the button. Controls height, horizontal padding, and font size.
	 * - `sm` — 28px tall, `text-xs`
	 * - `md` — 34px tall, `text-sm`
	 * @default "md"
	 */
	size?: ButtonVariantProps["size"];

	/**
	 * When true, applies fully rounded corners (`rounded-full`) for a pill-shaped button.
	 * @default false
	 */
	pill?: ButtonVariantProps["pill"];

	/**
	 * Optional icon rendered before the children. Pair the icon size with the button size:
	 * use `<Icon size="micro" />` with `size="sm"` and `<Icon size="mini" />` with `size="md"`.
	 */
	leadingIcon?: ReactNode;

	/**
	 * Optional icon rendered after the children. Follows the same sizing guidance as `leadingIcon`.
	 */
	trailingIcon?: ReactNode;

	/**
	 * When true:
	 * - a `<Spinner />` is absolutely positioned in the dead center of the button
	 * - the original icons and children remain in the DOM but are hidden via `opacity-0 invisible`,
	 *   preserving the button's intrinsic width and preventing layout shift
	 * - the underlying `<button>` is set to `disabled`, blocking clicks
	 * @default false
	 */
	loading?: boolean;

	/**
	 * Analytics identifier. Expanded by `trackingAttrs(trackingId)` into both
	 * `data-pendo-id` and `data-amp-track-id` attributes on the underlying `<button>`,
	 * so a single value flows to both Pendo and Amplitude pipelines.
	 */
	trackingId?: string;

	ref?: Ref<HTMLButtonElement>;
};

/**
 * Chromed buttons (`solid`/`outline`) require a semantic `color` — it drives the
 * background, border, and text via the CVA compound-variant matrix.
 */
type ButtonChromeProps = {
	/**
	 * Visual treatment of the button.
	 * - `solid`  — filled background with `text-inverse` (or `text-primary` for `color="inverse"`)
	 * - `outline` — transparent background with a colored border and matching text
	 * @default "solid"
	 */
	variant?: Exclude<ButtonVariantProps["variant"], "ghost">;

	/**
	 * Semantic intent of the button. Drives background, border, and text colors via the
	 * CVA compound-variant matrix. Required for `solid`/`outline` — there is no sensible default.
	 */
	color: ButtonColor;
};

/**
 * Ghost buttons are a transparent, chrome-free pass-through to the button API: no
 * background, border, or font of their own, so the caller's `className` controls the look.
 * Sizing still applies, so ghost buttons fall in line with `solid`/`outline` heights unless
 * overridden. Use to wrap bespoke clickable surfaces (menu rows, toggles, triggers) while
 * still getting `leadingIcon`/`trailingIcon`, `loading`, tracking, and button semantics.
 */
type ButtonGhostProps = {
	variant: "ghost";

	/** Optional for ghost — color is supplied by the caller's `className`, not the variant matrix. */
	color?: ButtonColor;
};

export type ButtonProps = ButtonCommonProps & (ButtonChromeProps | ButtonGhostProps);

export const Button: FC<ButtonProps> = ({
	color,
	variant,
	size,
	pill,
	leadingIcon,
	trailingIcon,
	loading,
	trackingId,
	className,
	children,
	disabled,
	type = "button",
	ref,
	...rest
}) => (
	<_BaseButton
		ref={ref}
		type={type}
		disabled={disabled || loading}
		aria-busy={loading || undefined}
		className={cn(buttonVariants({ color, variant, size, pill }), className)}
		trackingId={trackingId}
		{...rest}
	>
		<span className={cn("inline-flex items-center gap-2", loading && "opacity-0 invisible")}>
			{leadingIcon}
			{children}
			{trailingIcon}
		</span>
		{loading && (
			<span className="absolute inset-0 flex items-center justify-center">
				<Spinner size={buttonSizeToSpinnerSize[size ?? "md"]} />
			</span>
		)}
	</_BaseButton>
);
Button.displayName = "Button";
